import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as _ from 'lodash';
import * as moment from 'moment';

interface DetailRow { header: string; value: string; field_id?: string; showMore?: boolean; }

@Component({
  selector: 'app-outbox-information-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './outbox-information-panel.component.html',
  styleUrls: ['./outbox-information-panel.component.scss']
})
export class OutboxInformationPanelComponent implements OnChanges {
  @Input() feedData: any = {};
  @Input() feedId = '';
  @Input() brandDetails: any = {};
  @Input() isNotRespondedTab = false;

  profileDetails: DetailRow[] = [];
  transactionDetails: DetailRow[] = [];
  cohortDetails: DetailRow[] = [];
  notifactionDetails: DetailRow[] = [];

  private readonly notificationFields: Record<string, { title: string; date?: boolean }> = {
    notify_channel: { title: 'Notification Channel' },
    last_trial_attempt: { title: 'Attempts' },
    utm_medium: { title: 'Response Medium' },
    last_trial_sent: { title: 'Last Notified Date', date: true },
    utm_term: { title: 'Responded on' },
    trial_1: { title: 'Invitation Sent Date', date: true },
    trial_2: { title: 'Reminder1 Sent Date', date: true },
    trial_3: { title: 'Reminder2 Sent Date', date: true },
    trial_4: { title: 'Reminder3 Sent Date', date: true },
    trial_5: { title: 'Reminder4 Sent Date', date: true },
    trial_6: { title: 'Reminder5 Sent Date', date: true },
    trial_7: { title: 'Reminder6 Sent Date', date: true },
    trial_8: { title: 'Reminder7 Sent Date', date: true }
  };

  ngOnChanges(): void {
    const feed = _.get(this.feedData, 'feed', {});
    const processed = _.get(feed, 'litmus_processed_data', {});
    const combined = { ...feed, ...processed };
    const userId = _.get(feed, 'feed_by_id') || _.get(feed, 'user_id');
    const user = (_.get(this.feedData, 'users', []) as any[]).find(u => u._id === userId)
      || _.get(this.feedData, 'user', {}) || {};
    const fields = new Map<string, any>(
      (_.get(this.brandDetails, 'field_definitions', []) as any[]).map(f => [f.field_id, f])
    );
    const visible = (key: string) => fields.get(key)?.show_in_inbox_detail !== false;
    const label = (key: string, fallback: string) => fields.get(key)?.name || fallback;
    const first = (...values: any[]) => values.find(v => v !== null && v !== undefined && v !== '') ?? 'NA';
    const display = (v: any) => Array.isArray(v) ? v.join(', ') : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v));
    const profile: Array<[string, string, any]> = [
      ['name', 'Customer Name', first(user.name, combined.tag_private_user_name, _.get(feed, 'feedback_request_data.tag_private_user_name'), 'Anonymous')],
      ['email_address', 'Email Address', first(user.email_address, combined.tag_private_email_address)],
      ['phone_number', 'Phone Number', first(user.phone_number, combined.tag_private_phone_number)],
      ['customer_id', 'Customer id', first(user.customer_id, user.tag_customer_id, combined.tag_private_customer_id, combined.tag_customer_id, combined.customer_reference_id)]
    ];
    this.profileDetails = profile.filter(([key]) => visible(key))
      .map(([key, fallback, value]) => ({ header: label(key, fallback), value: display(value) }));

    const tags = _.get(this.feedData, 'tags', {});
    const transactionSource = !_.isEmpty(tags) ? tags : combined;
    this.transactionDetails = Array.from(fields.entries())
      .filter(([key, definition]) => key !== 'tag_private_customer_id' && key !== 'tag_customer_id'
        && !key.startsWith('ltms_custom_field_') && definition.show_in_inbox_detail !== false
        && Object.prototype.hasOwnProperty.call(transactionSource, key)
        && transactionSource[key] !== null && transactionSource[key] !== '')
      .map(([key, definition]) => ({
        field_id: key,
        header: definition.name || key,
        value: definition.type === 'date' ||
          key === 'feedback_request_date' ||
          this.isISODate(transactionSource[key])
          ? this.formatDate(transactionSource[key], definition.format)
          : display(transactionSource[key])
      }))
      .sort((a, b) => a.header.localeCompare(b.header));

    // The flat feed is the fallback; nested processed fields override it.
    this.notifactionDetails = Object.entries(this.notificationFields)
      .filter(([key]) => combined[key] !== null && combined[key] !== undefined && combined[key] !== '')
      .map(([key, config]) => {
        let value = combined[key];
        if (config.date) value = this.formatDate(value);
        else if (typeof value === 'string') value = value.replace(/trial_(\d+)/gi, 'attempt $1');
        return { header: config.title, value: display(value) };
      });
  }

  private formatDate(value: any, format = "DD MMM 'YY HH:mm:ss"): string {
    const date = moment.utc(value);
    if (!date.isValid()) return String(value ?? 'NA');
    const offset = _.get(this.brandDetails, 'default_time_zone.offset');
    const timezone = _.get(this.brandDetails, 'default_time_zone.timezone');
    if (offset !== undefined && offset !== null && timezone) {
      return `${date.utcOffset(-Number(offset)).format(format)} (${timezone})`;
    }
    return date.format(format) + ' (UTC)';
  }
  private isISODate(value: any): boolean {
    return typeof value === 'string' &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
      moment.parseZone(value, moment.ISO_8601, true).isValid();
  }
  toggleShowMore(row: DetailRow): void { row.showMore = !row.showMore; }
}
